// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { resolveId } from '@medplum/core';
import type {
  ChargeItem,
  Claim,
  Coverage,
  CoverageEligibilityResponse,
  Encounter,
  Observation,
  Organization,
  Patient,
} from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  CHARGE_ITEM_SOURCE_SYSTEM,
  getMrn,
  resolveBillingPathway,
  STRIPE_PAYMENT_INTENT_IDENTIFIER_SYSTEM,
} from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useState } from 'react';
import { getInvoiceNumber } from '../utils/chargeitems';
import { showErrorNotification } from '../utils/notifications';
import { patientDisplayName } from '../utils/patient-display';

export type PaymentsVariant = 'upcoming' | 'outstanding' | 'complete' | 'archived';
export type PaymentsTimeframe =
  | 'this-month'
  | 'last-7-days'
  | 'last-30-days'
  | 'last-90-days'
  | 'last-3-months'
  | 'last-6-months'
  | 'last-12-months'
  | 'year-to-date'
  | 'all'
  | 'custom';
export type PaymentSource = 'all' | 'self-pay' | 'insured';
export type InsuredBalanceStatus = 'Awaiting balance' | 'Balance recorded' | 'Paid';
export type InsuredClaimStatus = 'toSubmit' | 'sent' | 'record';

const INSURED_BALANCE_SOURCE = 'insurance-patient-responsibility';
const PATIENT_BILL_CODE_SYSTEM = 'http://polarissleep.com/charge-item-definitions';
const PATIENT_BILL_IDENTIFIER_SYSTEM = 'https://polarissleep.com/identifiers/patient-bill';
const CHARGE_DUE_DATE_EXTENSION_URL = 'https://polarissleep.com/fhir/StructureDefinition/charge-due-date';
const INSURANCE_CONTRIBUTION_EXTENSION_URL = 'https://polarissleep.com/fhir/StructureDefinition/insurance-contribution';
const SELF_PAY_TAG_SYSTEM = 'http://polarissleep.com/fhir/tags';
const SELF_PAY_TAG_CODE = 'self-pay';
export const PAYMENT_MANUAL_PAID_EXTENSION_URL =
  'https://polarissleep.com/fhir/StructureDefinition/payment-manual-paid';
export const PAYMENT_ARCHIVED_EXTENSION_URL = 'https://polarissleep.com/fhir/StructureDefinition/payment-archived';
export const PAYMENT_OUTREACH_ATTEMPTS_EXTENSION_URL =
  'https://polarissleep.com/fhir/StructureDefinition/payment-outreach-attempts';
export const PAYMENT_HISTORY_EXTENSION_URL = 'https://polarissleep.com/fhir/StructureDefinition/payment-history';
export const REFUNDED_AMOUNT_EXTENSION_URL = 'https://polarissleep.com/fhir/StructureDefinition/refunded-amount-cents';
export const ESTIMATED_AMOUNT_EXTENSION_URL =
  'https://polarissleep.com/fhir/StructureDefinition/estimated-charge-amount';
export const PAYMENT_RECEIVED_AMOUNT_EXTENSION_URL =
  'https://polarissleep.com/fhir/StructureDefinition/payment-received-amount-cents';
export const PAYMENT_CLAIM_STATUS_EXTENSION_URL =
  'https://polarissleep.com/fhir/StructureDefinition/payment-claim-status';

export interface PaymentChargesFilters {
  timeframe: PaymentsTimeframe;
  source?: PaymentSource;
  status?: string | null;
  patientName?: string;
  encounterType?: string | null;
  search?: string;
  from?: string | null;
  to?: string | null;
}

export interface PaymentChargeEntry {
  chargeItemId: string;
  created: string;
  chargeId: string;
  status: 'Planned' | 'Billed' | 'Paid' | 'Refunded' | 'Archived';
  patientId: string;
  patientIdentifier: string;
  patientName: string;
  encounterId?: string;
  appointmentId?: string;
  claimId?: string;
  outreachAttempts: number;
  encounterType?: string;
  dateOfService?: string;
  billedCents: number;
  estimatedBilledCents?: number;
  receivedCents: number;
  balanceCents: number;
  selfPay: boolean;
  source: Exclude<PaymentSource, 'all'>;
  payer?: string;
  dueDate?: string;
  insuranceContributionCents?: number;
  balanceStatus?: InsuredBalanceStatus;
  claimStatus?: InsuredClaimStatus;
  hasCapturedPayment: boolean;
  refundedCents: number;
  history: Array<{ date: string; event: string }>;
  periodStart?: string;
  periodEnd?: string;
  rpmDetails?: {
    transmissionDays: number;
    careManagementMinutes: number;
    chargeLines: Array<{ code: '99454' | '99457'; amountCents?: number; met: boolean }>;
  };
}

export interface UsePaymentChargesArgs {
  variant: PaymentsVariant;
  page: number;
  pageSize: number;
  filters: PaymentChargesFilters;
}

export interface UsePaymentChargesResult {
  entries: PaymentChargeEntry[];
  total: number;
  loading: boolean;
}

function timeframeWindow(timeframe: PaymentsTimeframe, now: Date): { ge?: string; le?: string } {
  const fmt = (d: Date): string => d.toISOString().slice(0, 10);
  if (timeframe === 'all') {
    return {};
  }
  if (timeframe === 'last-90-days') {
    const start = new Date(now);
    start.setUTCDate(start.getUTCDate() - 90);
    return { ge: fmt(start) };
  }
  if (timeframe === 'last-7-days' || timeframe === 'last-30-days') {
    const start = new Date(now);
    start.setUTCDate(start.getUTCDate() - (timeframe === 'last-7-days' ? 7 : 30));
    return { ge: fmt(start) };
  }
  if (timeframe === 'last-3-months' || timeframe === 'last-6-months' || timeframe === 'last-12-months') {
    const start = new Date(now);
    start.setUTCMonth(start.getUTCMonth() - Number(timeframe.split('-')[1]));
    return { ge: fmt(start) };
  }
  if (timeframe === 'year-to-date') {
    return { ge: `${now.getUTCFullYear()}-01-01` };
  }
  if (timeframe === 'custom') {
    return {};
  }
  if (timeframe === 'this-month') {
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0));
    return { ge: fmt(start), le: fmt(end) };
  }
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
  return { ge: fmt(start), le: fmt(end) };
}

function hasStripePaymentIntent(ci: ChargeItem): boolean {
  return !!ci.identifier?.some((i) => i.system === STRIPE_PAYMENT_INTENT_IDENTIFIER_SYSTEM);
}

function hasManualPaidMarker(ci: ChargeItem): boolean {
  return (
    ci.extension?.some((extension) => extension.url === PAYMENT_MANUAL_PAID_EXTENSION_URL && extension.valueBoolean) ??
    false
  );
}

function isArchived(ci: ChargeItem): boolean {
  return (
    ci.extension?.some((extension) => extension.url === PAYMENT_ARCHIVED_EXTENSION_URL && extension.valueBoolean) ??
    false
  );
}

function isPaid(ci: ChargeItem): boolean {
  if (hasManualPaidMarker(ci)) {
    return true;
  }
  if (!hasStripePaymentIntent(ci)) {
    return false;
  }
  const billedCents = dollarsToCents(ci.priceOverride?.value);
  const receivedCents = extensionInteger(ci, PAYMENT_RECEIVED_AMOUNT_EXTENSION_URL) ?? billedCents;
  const discountedCents = ci.extension
    ?.find((extension) => extension.url === 'https://polarissleep.com/fhir/StructureDefinition/applied-discount')
    ?.extension?.find((extension) => extension.url === 'discountedAmount')?.valueInteger;
  return receivedCents >= (discountedCents ?? billedCents);
}

function isHistoricalPaid(ci: ChargeItem): boolean {
  return isPaid(ci) || (ci.status as string) === 'posted' || (ci.status as string) === 'completed';
}

function isInsuredBalanceCharge(ci: ChargeItem): boolean {
  const hasBalanceSourceTag = ci.meta?.tag?.some(
    (tag) => tag.system === CHARGE_ITEM_SOURCE_SYSTEM && tag.code === INSURED_BALANCE_SOURCE
  );
  if (!hasBalanceSourceTag) {
    return false;
  }
  const hasPatientBillCode = ci.code?.coding?.some(
    (coding) => coding.system === PATIENT_BILL_CODE_SYSTEM && coding.code === INSURED_BALANCE_SOURCE
  );
  const hasPatientBillIdentifier = ci.identifier?.some(
    (identifier) => identifier.system === PATIENT_BILL_IDENTIFIER_SYSTEM
  );
  return hasPatientBillCode === true || hasPatientBillIdentifier === true;
}

function extensionDate(charge: ChargeItem, url: string): string | undefined {
  return charge.extension?.find((extension) => extension.url === url)?.valueDateTime;
}

function extensionMoneyCents(charge: ChargeItem, url: string): number | undefined {
  const value = charge.extension?.find((extension) => extension.url === url)?.valueMoney?.value;
  return value === undefined || !Number.isFinite(value) ? undefined : Math.round(value * 100);
}

function extensionInteger(charge: ChargeItem, url: string): number | undefined {
  return charge.extension?.find((extension) => extension.url === url)?.valueInteger;
}

function extensionCode(charge: ChargeItem, url: string): string | undefined {
  return charge.extension?.find((extension) => extension.url === url)?.valueCode;
}

function history(charge: ChargeItem): Array<{ date: string; event: string }> {
  return (charge.extension ?? [])
    .filter((extension) => extension.url === PAYMENT_HISTORY_EXTENSION_URL)
    .map((extension) => ({
      date: extension.extension?.find((item) => item.url === 'date')?.valueDateTime ?? charge.meta?.lastUpdated ?? '',
      event: extension.extension?.find((item) => item.url === 'event')?.valueString ?? 'Updated',
    }));
}

function isRpmCharge(charge: ChargeItem): boolean {
  return (
    charge.identifier?.some((identifier) => identifier.system === 'https://polarissleep.com/fhir/rpm-month') ?? false
  );
}

function isSubscriptionCharge(charge: ChargeItem): boolean {
  const text = [
    charge.code?.text,
    ...(charge.code?.coding ?? []).flatMap((coding) => [coding.code, coding.display]),
    ...(charge.definitionCanonical ?? []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return text.includes('subscription');
}

function observationValue(observations: Observation[], code: string, start?: string, end?: string): number {
  const matching = observations.filter((observation) => {
    if (!observation.code?.coding?.some((coding) => coding.code === code)) {
      return false;
    }
    const date = observation.effectiveDateTime ?? observation.effectivePeriod?.start;
    return !!date && (!start || date >= start) && (!end || date <= end);
  });
  return matching.reduce((total, observation) => total + (observation.valueInteger ?? 0), 0);
}

function payerName(eligibility: CoverageEligibilityResponse | undefined): string {
  const insurer = eligibility?.insurer;
  return insurer?.display ?? insurer?.reference ?? 'Insurance';
}

function coveragePayerName(coverage: Coverage | undefined, payorMap: Map<string, Organization>): string | undefined {
  const payor = coverage?.payor?.[0];
  if (!payor) {
    return undefined;
  }
  const payorId = payor.reference ? resolveId(payor) : undefined;
  return (payorId ? payorMap.get(payorId)?.name : undefined) ?? payor.display;
}

export function derivePaymentSource(patient: Patient | undefined, charge: ChargeItem): Exclude<PaymentSource, 'all'> {
  if (isInsuredBalanceCharge(charge)) {
    return 'insured';
  }

  const dateOfService = charge.occurrenceDateTime ?? charge.occurrencePeriod?.start;
  if (patient && dateOfService) {
    try {
      const pathway = resolveBillingPathway(patient, dateOfService);
      if (pathway === 'SELF-PAY') {
        return 'self-pay';
      }
      if (pathway === 'INSURANCE') {
        return 'insured';
      }
    } catch {
      // Fall through to the legacy marker for older or ambiguous records.
    }
  }

  const selfPay = patient?.meta?.tag?.some(
    (tag) => tag.system === SELF_PAY_TAG_SYSTEM && tag.code === SELF_PAY_TAG_CODE
  );
  return selfPay ? 'self-pay' : 'insured';
}

function insuredChargeStatus(status: PaymentChargeEntry['status']): InsuredBalanceStatus {
  return status === 'Paid' ? 'Paid' : 'Balance recorded';
}

function insuredEntryStatus(charge: ChargeItem | undefined): InsuredBalanceStatus {
  if (!charge) {
    return 'Awaiting balance';
  }
  return isPaid(charge) ? 'Paid' : 'Balance recorded';
}

function dateInWindow(date: string | undefined, window: { ge?: string; le?: string }): boolean {
  if (!date) {
    return false;
  }
  const day = date.slice(0, 10);
  return (!window.ge || day >= window.ge) && (!window.le || day <= window.le);
}

function deriveStatus(ci: ChargeItem): PaymentChargeEntry['status'] {
  if (isArchived(ci)) {
    return 'Archived';
  }
  if (ci.status === 'aborted' && hasStripePaymentIntent(ci)) {
    return 'Refunded';
  }
  // Older chart entries used the ledger vocabulary `posted`/`completed`
  // instead of ChargeItem's current `billed` status. They are historical
  // payment records and must remain visible even when no current price exists.
  if (isHistoricalPaid(ci)) {
    return 'Paid';
  }
  if (ci.status === 'planned') {
    return 'Planned';
  }
  return 'Billed';
}

function dollarsToCents(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) {
    return 0;
  }
  return Math.round(value * 100);
}

function patientBalanceCents(
  status: PaymentChargeEntry['status'],
  billedCents: number,
  receivedCents: number,
  insuranceContributionCents: number | undefined,
  insuredBalance: boolean
): number {
  if (status === 'Paid' || status === 'Refunded') {
    return 0;
  }
  const remainingCents = billedCents - receivedCents;
  if (insuredBalance && insuranceContributionCents !== undefined) {
    return Math.max(0, remainingCents - insuranceContributionCents);
  }
  return Math.max(0, remainingCents);
}

function variantStatusFilter(variant: PaymentsVariant): Set<string> {
  switch (variant) {
    case 'upcoming':
      return new Set(['planned', 'billable']);
    case 'outstanding':
      return new Set(['billable', 'billed']);
    case 'complete':
      return new Set(['billed', 'aborted', 'posted', 'completed']);
    case 'archived':
      return new Set(['billable', 'billed', 'aborted']);
  }
}

function lifecycleStatus(entry: PaymentChargeEntry, variant: PaymentsVariant): string {
  if (entry.status === 'Archived') {
    return 'Archived with Balance';
  }
  if (entry.status === 'Refunded') {
    return 'Refunded';
  }
  if (entry.status === 'Paid') {
    return 'Paid';
  }
  if (entry.status === 'Planned') {
    return 'Scheduled';
  }
  if (entry.source === 'insured') {
    if (variant === 'upcoming') {
      if (entry.claimStatus === 'toSubmit') {
        return 'Awaiting Claim Submission';
      }
      if (entry.claimStatus === 'sent') {
        return 'Awaiting Claim Return';
      }
      if (entry.claimStatus === 'record') {
        return 'Record Balance';
      }
      const hasSubmitted = entry.history.some((item) => item.event.toLowerCase().includes('claim submitted'));
      const hasReturned = entry.history.some((item) => item.event.toLowerCase().includes('claim returned'));
      if (!hasSubmitted) {
        return 'Awaiting Claim Submission';
      }
      if (!hasReturned) {
        return 'Awaiting Claim Return';
      }
      return 'Record Balance';
    }
    if (entry.balanceStatus === 'Awaiting balance') {
      const hasSubmitted = entry.history.some((item) => item.event.toLowerCase().includes('claim submitted'));
      const hasReturned = entry.history.some((item) => item.event.toLowerCase().includes('claim returned'));
      if (!hasSubmitted) {
        return 'Awaiting Claim Submission';
      }
      if (!hasReturned) {
        return 'Awaiting Claim Return';
      }
      return 'Record Balance';
    }
    return 'Awaiting Payment';
  }
  if (variant === 'upcoming') {
    return 'Collect Payment';
  }
  return 'Awaiting Payment';
}

export function usePaymentCharges(args: UsePaymentChargesArgs): UsePaymentChargesResult {
  const { variant, page, pageSize, filters } = args;
  const { timeframe, source = 'all', status, patientName, encounterType, search, from, to } = filters;
  const medplum = useMedplum();
  const [entries, setEntries] = useState<PaymentChargeEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        _count: '200',
        _sort: '-_lastUpdated',
      });
      params.append('_include', 'ChargeItem:subject');
      params.append('_include', 'ChargeItem:context');

      const now = new Date();
      const window = timeframeWindow(timeframe, now);
      const effectiveWindow = timeframe === 'custom' ? { ge: from ?? undefined, le: to ?? undefined } : window;

      // The design separates ledger sections by payment lifecycle, not by whether
      // the date of service is in the future or past. The date control remains a
      // user-selected visibility filter across every section.
      if (effectiveWindow.ge) {
        params.append('_lastUpdated', `ge${effectiveWindow.ge}`);
      }
      if (effectiveWindow.le) {
        params.append('_lastUpdated', `le${effectiveWindow.le}`);
      }

      const bundle = await medplum.searchResources('ChargeItem', params);
      const allowedStatuses = variantStatusFilter(variant);
      const charges: ChargeItem[] = bundle.filter((r) => {
        if (r.resourceType !== 'ChargeItem' || isSubscriptionCharge(r)) {
          return false;
        }
        if (!dateInWindow(r.meta?.lastUpdated, effectiveWindow)) {
          return false;
        }
        return (
          allowedStatuses.has(r.status ?? '') ||
          ((variant === 'complete' || variant === 'archived') && isHistoricalPaid(r)) ||
          (variant === 'archived' && isArchived(r))
        );
      });
      const patients: Patient[] = (bundle as unknown as Patient[]).filter((r) => r.resourceType === 'Patient');
      const encounters: Encounter[] = (bundle as unknown as Encounter[]).filter((r) => r.resourceType === 'Encounter');
      const claims = (
        variant === 'upcoming' && source !== 'self-pay'
          ? await medplum.searchResources('Claim', { _count: '200', _sort: '-created' })
          : []
      ) as Claim[];
      const coverages = (await medplum.searchResources('Coverage', {
        status: 'active',
        _count: '200',
        _sort: '-_lastUpdated',
        _include: 'Coverage:payor',
      })) as Array<Coverage | Organization>;
      const coverageMap = new Map<string, Coverage>();
      const payorMap = new Map<string, Organization>();
      for (const resource of coverages) {
        if (resource.resourceType === 'Coverage' && resource.id) {
          const patientId = resolveId(resource.beneficiary);
          if (patientId && !coverageMap.has(patientId)) {
            coverageMap.set(patientId, resource);
          }
        } else if (resource.resourceType === 'Organization' && resource.id) {
          payorMap.set(resource.id, resource);
        }
      }
      const patientMap = new Map<string, Patient>();
      for (const p of patients) {
        if (p.id) {
          patientMap.set(p.id, p);
        }
      }
      const encounterMap = new Map<string, Encounter>();
      for (const e of encounters) {
        if (e.id) {
          encounterMap.set(e.id, e);
        }
      }
      const claimByEncounter = new Map<string, Claim>();
      for (const claim of claims) {
        if (!claim.id) {
          continue;
        }
        const encounterId = claim.item
          ?.flatMap((item) => item.encounter ?? [])
          .map(resolveId)
          .find(Boolean);
        if (encounterId && !claimByEncounter.has(encounterId)) {
          claimByEncounter.set(encounterId, claim);
        }
      }

      const filteredByPaid = charges.filter((ci) => {
        const chargeSource = derivePaymentSource(patientMap.get(resolveId(ci.subject) ?? ''), ci);
        if (chargeSource === 'self-pay' && isRpmCharge(ci)) {
          return false;
        }
        if (source !== 'all' && source !== chargeSource) {
          return false;
        }
        // The design's Upcoming queue contains insured claims only. Self-pay is
        // charged automatically at service and appears only if it becomes an
        // outstanding balance or a completed payment.
        if (variant === 'upcoming' && chargeSource === 'self-pay') {
          return false;
        }
        if (variant === 'outstanding' && chargeSource === 'insured' && !isInsuredBalanceCharge(ci)) {
          return false;
        }
        const paid = isHistoricalPaid(ci);
        if (variant === 'archived' && !isArchived(ci)) {
          return false;
        }
        if (variant !== 'archived' && isArchived(ci)) {
          return false;
        }
        if (variant === 'outstanding') {
          return !paid;
        }
        if (variant === 'upcoming') {
          return !paid;
        }
        if (variant === 'complete') {
          return paid;
        }
        return true;
      });

      const mapped: PaymentChargeEntry[] = filteredByPaid.map((ci) => {
        const status = deriveStatus(ci);
        const patientId = resolveId(ci.subject) ?? '';
        const patient = patientMap.get(patientId);
        const chargeSource = derivePaymentSource(patient, ci);
        const billedCents = dollarsToCents(ci.priceOverride?.value);
        const refundedCents = extensionInteger(ci, REFUNDED_AMOUNT_EXTENSION_URL) ?? 0;
        const capturedCents = extensionInteger(ci, PAYMENT_RECEIVED_AMOUNT_EXTENSION_URL);
        const receivedCents = capturedCents ?? (status === 'Paid' || status === 'Refunded' ? billedCents : 0);
        const insuranceContributionCents = extensionMoneyCents(ci, INSURANCE_CONTRIBUTION_EXTENSION_URL);
        const encounterId = resolveId(ci.context);
        const encounter = encounterId ? encounterMap.get(encounterId) : undefined;
        const appointmentId = ci.service?.map((service) => resolveId(service)).find(Boolean);
        const encounterType = encounter?.type?.[0]?.text ?? encounter?.serviceType?.text ?? '—';
        return {
          chargeItemId: ci.id ?? '',
          created: ci.meta?.lastUpdated ?? '',
          chargeId: getInvoiceNumber(ci),
          status,
          patientId,
          patientIdentifier: getMrn(patient) ?? patientId,
          patientName: patientDisplayName(patient, ci.subject as unknown as never),
          encounterId,
          appointmentId,
          claimId: encounterId ? claimByEncounter.get(encounterId)?.id : undefined,
          outreachAttempts: extensionInteger(ci, PAYMENT_OUTREACH_ATTEMPTS_EXTENSION_URL) ?? 0,
          encounterType,
          dateOfService: ci.occurrenceDateTime ?? ci.occurrencePeriod?.start,
          billedCents,
          estimatedBilledCents: extensionMoneyCents(ci, ESTIMATED_AMOUNT_EXTENSION_URL),
          receivedCents,
          balanceCents: patientBalanceCents(
            status,
            billedCents,
            receivedCents,
            insuranceContributionCents,
            isInsuredBalanceCharge(ci)
          ),
          selfPay: chargeSource === 'self-pay',
          source: chargeSource,
          payer:
            chargeSource === 'insured'
              ? (coveragePayerName(coverageMap.get(patientId), payorMap) ?? 'Insurance')
              : 'Self-pay',
          dueDate: extensionDate(ci, CHARGE_DUE_DATE_EXTENSION_URL),
          insuranceContributionCents,
          balanceStatus: isInsuredBalanceCharge(ci) ? insuredChargeStatus(status) : undefined,
          claimStatus: extensionCode(ci, PAYMENT_CLAIM_STATUS_EXTENSION_URL) as InsuredClaimStatus | undefined,
          hasCapturedPayment: hasStripePaymentIntent(ci),
          refundedCents,
          history: history(ci),
          periodStart: ci.occurrencePeriod?.start,
          periodEnd: ci.occurrencePeriod?.end,
        };
      });

      const mappedWithRpmDetails = await Promise.all(
        mapped.map(async (entry) => {
          const charge = filteredByPaid.find((candidate) => candidate.id === entry.chargeItemId);
          if (!charge || entry.source !== 'insured' || !isRpmCharge(charge)) {
            return entry;
          }
          const observations = (await medplum.searchResources('Observation', {
            subject: `Patient/${entry.patientId}`,
            _count: '50',
          })) as Observation[];
          const transmissionDays = observationValue(
            observations,
            'adherence-nights-30-day',
            entry.periodStart,
            entry.periodEnd
          );
          const careManagementMinutes =
            observationValue(observations, 'async-monitoring-minutes', entry.periodStart, entry.periodEnd) +
            observationValue(observations, 'sync-contact-minutes', entry.periodStart, entry.periodEnd);
          const chargeLines: Array<{ code: '99454' | '99457'; amountCents?: number; met: boolean }> = [
            { code: '99454', met: transmissionDays >= 16 },
            { code: '99457', amountCents: entry.billedCents, met: careManagementMinutes >= 20 },
          ];
          return {
            ...entry,
            rpmDetails: {
              transmissionDays,
              careManagementMinutes,
              chargeLines,
            },
          };
        })
      );

      if (source !== 'self-pay' && variant === 'outstanding') {
        const encounters = await medplum.searchResources('Encounter', {
          status: 'finished',
          _count: '200',
          _sort: '-date',
          _include: 'Encounter:subject',
        });
        const encounterResources = (encounters as unknown as Array<Encounter | Patient>).filter(
          (resource): resource is Encounter | Patient =>
            resource.resourceType === 'Encounter' || resource.resourceType === 'Patient'
        );
        const includedPatients = new Map<string, Patient>();
        for (const resource of encounterResources) {
          if (resource.resourceType === 'Patient' && resource.id) {
            includedPatients.set(resource.id, resource);
          }
        }
        const balanceByEncounter = new Map<string, ChargeItem>();
        for (const charge of charges.filter(isInsuredBalanceCharge)) {
          const encounterId = resolveId(charge.context);
          if (encounterId) {
            balanceByEncounter.set(encounterId, charge);
          }
        }
        const eligibilityResponses = await medplum.searchResources('CoverageEligibilityResponse', {
          outcome: 'complete',
          _count: '200',
          _sort: '-created',
        });
        const activeEligibilityByPatient = new Map<string, CoverageEligibilityResponse>();
        for (const resource of eligibilityResponses as CoverageEligibilityResponse[]) {
          if (resource.resourceType !== 'CoverageEligibilityResponse' || resource.insurance?.[0]?.inforce !== true) {
            continue;
          }
          const patientId = resolveId(resource.patient);
          if (patientId && !activeEligibilityByPatient.has(patientId)) {
            activeEligibilityByPatient.set(patientId, resource);
          }
        }
        const existingEncounterIds = new Set(mapped.map((entry) => entry.encounterId).filter(Boolean));
        const insuranceEntries = await Promise.all(
          encounterResources
            .filter((resource): resource is Encounter => resource.resourceType === 'Encounter' && !!resource.id)
            .filter((encounter) => dateInWindow(encounter.meta?.lastUpdated, effectiveWindow))
            // Outstanding contains only explicitly recorded patient balances.
            // An eligible finished encounter without a balance ChargeItem is
            // not yet billable and must stay out of this ledger section.
            .filter((encounter) => balanceByEncounter.has(encounter.id as string))
            .map(async (encounter): Promise<PaymentChargeEntry | undefined> => {
              const patientId = resolveId(encounter.subject);
              if (!patientId || !encounter.subject || existingEncounterIds.has(encounter.id as string)) {
                return undefined;
              }
              const patient =
                includedPatients.get(patientId) ?? ((await medplum.readReference(encounter.subject)) as Patient);
              const eligibility = activeEligibilityByPatient.get(patientId);
              if (!eligibility) {
                return undefined;
              }
              const charge = balanceByEncounter.get(encounter.id as string);
              if (source === 'insured' && charge && isPaid(charge)) {
                return undefined;
              }
              const amountCents = dollarsToCents(charge?.priceOverride?.value);
              const insuranceContributionCents = charge
                ? extensionMoneyCents(charge, INSURANCE_CONTRIBUTION_EXTENSION_URL)
                : undefined;
              const chargeStatus = charge ? deriveStatus(charge) : 'Billed';
              return {
                chargeItemId: charge?.id ?? `encounter:${encounter.id}`,
                created: charge?.meta?.lastUpdated ?? encounter.meta?.lastUpdated ?? '',
                chargeId: charge ? getInvoiceNumber(charge) : '—',
                status: chargeStatus,
                patientId,
                patientIdentifier: getMrn(patient as Patient) ?? patientId,
                patientName: patientDisplayName(patient as Patient, encounter.subject as never),
                encounterId: encounter.id,
                claimId: claimByEncounter.get(encounter.id as string)?.id,
                outreachAttempts: 0,
                encounterType: encounter.type?.[0]?.text ?? encounter.serviceType?.text ?? '—',
                dateOfService: encounter.period?.start ?? encounter.meta?.lastUpdated,
                billedCents: amountCents,
                receivedCents: charge && isPaid(charge) ? amountCents : 0,
                balanceCents: patientBalanceCents(
                  chargeStatus,
                  amountCents,
                  charge && isPaid(charge) ? amountCents : 0,
                  insuranceContributionCents,
                  true
                ),
                selfPay: false,
                source: 'insured',
                payer: payerName(eligibility),
                dueDate: charge ? extensionDate(charge, CHARGE_DUE_DATE_EXTENSION_URL) : undefined,
                insuranceContributionCents,
                balanceStatus: insuredEntryStatus(charge),
                hasCapturedPayment: charge ? hasStripePaymentIntent(charge) : false,
                refundedCents: charge ? (extensionInteger(charge, REFUNDED_AMOUNT_EXTENSION_URL) ?? 0) : 0,
                history: charge ? history(charge) : [],
              };
            })
        );
        mapped.push(...insuranceEntries.filter((entry): entry is PaymentChargeEntry => entry !== undefined));
      }

      // Appointment placeholders can be planned before a billable ChargeItem exists.
      // They are not patient-payment ledger rows (intake/check-in is free).
      const ledgerMapped = mappedWithRpmDetails.filter(
        (entry) => !(entry.status === 'Planned' && entry.billedCents <= 0 && entry.encounterType === 'Check-in')
      );
      const allMapped = [
        ...ledgerMapped,
        ...mapped.filter(
          (entry) =>
            !ledgerMapped.some((ledgerEntry) => ledgerEntry.chargeItemId === entry.chargeItemId) &&
            !(entry.status === 'Planned' && entry.billedCents <= 0 && entry.encounterType === 'Check-in')
        ),
      ];
      const sourceFiltered = (
        source === 'all' ? allMapped : allMapped.filter((entry) => entry.source === source)
      ).filter((entry) => {
        if (status && lifecycleStatus(entry, variant) !== status) {
          return false;
        }
        if (patientName && !entry.patientName.toLowerCase().includes(patientName.toLowerCase())) {
          return false;
        }
        if (encounterType && entry.encounterType !== encounterType) {
          return false;
        }
        if (search) {
          const query = search.toLowerCase();
          const searchable = [
            entry.patientName,
            entry.patientIdentifier,
            entry.chargeId,
            entry.encounterType,
            entry.payer,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          if (!searchable.includes(query)) {
            return false;
          }
        }
        return true;
      });

      setTotal(sourceFiltered.length);
      const startIndex = (page - 1) * pageSize;
      setEntries(sourceFiltered.slice(startIndex, startIndex + pageSize));
    } catch (err) {
      setEntries([]);
      setTotal(0);
      showErrorNotification(err);
      Sentry.captureException(err);
    } finally {
      setLoading(false);
    }
  }, [medplum, variant, page, pageSize, timeframe, source, status, patientName, encounterType, search, from, to]);

  useEffect(() => {
    fetchData().catch(() => undefined);
  }, [fetchData]);

  return { entries, total, loading };
}
